import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Image, Text,
} from 'grommet';
import { FormPrevious } from 'grommet-icons';
import { Seo, Container } from '@Components/Control';
import { useAppTheme } from '@Components/Context';
import { localAsset } from '@Helpers/asset';
import { paths } from '@Components/configs';
import Footer from '../../Navigation/Footer';
import {
  StyledForm, StyledTitle, StyledAnchor,
} from './StyledNoAuthComponent';


const BackButton = ({
  path, onClick, children,
}) => {
  if (onClick) {
    return (
      <StyledAnchor onClick={onClick}>
        {children}
      </StyledAnchor>
    );
  }

  return (
    <StyledAnchor path={path}>
      {children}
    </StyledAnchor>
  );
};

BackButton.propTypes = {
  path: PropTypes.string,
  onClick: PropTypes.func,
  children: PropTypes.node.isRequired,
};

const NoAuthLayout = ({
  seoConfig = null,
  small,
  withBackButton = false,
  backButtonLabel = 'Back',
  backButtonPath = paths.login,
  backButtonClick = null,
  bottomPad = '0',
  title,
  subTitle = null,
  children,
  customWidth = null,
  customTitle = false,
  customGap = null,
  justifyContent = 'center',
  contentTopMargin = null,
  removeLowerPad = false,
  altHeaderStyle = false,
  hideFooter = false,
  ...rest
}) => {
  const { themeConfig, noAuthPages, setDarkMode } = useAppTheme();
  React.useEffect(() => {
    setDarkMode(noAuthPages.darkBg);
  }, [noAuthPages.darkBg, setDarkMode]);

  const theme = noAuthPages.darkBg ? themeConfig.darkTheme : themeConfig.lightTheme;
  const logoSrc = noAuthPages.darkBg ? 'images/logo/logo-image-only-white.svg' : 'images/logo/logo-image-only-color.svg';

  const renderHeader = () => {
    if (customTitle) return null;
    if (altHeaderStyle) {
      return (
        <Box direction="column" gap="0.25rem" width="100%" justify="start">
          {withBackButton && (
            <BackButton
              path={backButtonClick ? null : backButtonPath}
              onClick={backButtonClick}
            >
              <Box direction="row" align="center" gap="0.15rem">
                <Box pad={{ top: '0.1rem' }}>
                  <FormPrevious size="0.85rem" color={theme.navigation.iconHoverColor} />
                </Box>
                <Text size="0.85rem">{backButtonLabel}</Text>
              </Box>
            </BackButton>
          )}
          <Box direction="row" align="center" justify="between">
            <Text weight={700} size="1.5rem" color={theme.page.primaryText}>
              {title}
            </Text>
            <Box width="1.65rem" height="1.65rem">
              <Image
                src={localAsset(logoSrc)}
                alt="Redflag Logo"
                fit="contain"
                width="1.65rem"
                height="1.65rem"
              />
            </Box>
          </Box>
          {subTitle && (
            <Text size="0.85rem" color={theme.page.primaryText}>
              {subTitle}
            </Text>
          )}
        </Box>
      );
    }
    return (
      <Box width="100%" justify="start">
        {withBackButton && (
          <BackButton
            path={backButtonClick ? null : backButtonPath}
            onClick={backButtonClick}
          >
            <Box direction="row" align="center" gap="0.15rem">
              <Box pad={{ top: '0.1rem' }}>
                <FormPrevious size="0.85rem" color={theme.navigation.iconHoverColor} />
              </Box>
              <Text size="0.85rem">{backButtonLabel}</Text>
            </Box>
          </BackButton>
        )}
        <Box width="100%" direction="column" gap="0.65rem" align="center" justify="center">
          <Box width="2rem" height="2rem">
            <Image
              src={localAsset(logoSrc)}
              alt={`${title} icon`}
              fit="contain"
              width="2rem"
              height="2rem"
            />
          </Box>
          <StyledTitle size="1.35rem" textAlign="center" color={theme.page.primaryText}>
            {title}
          </StyledTitle>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      flex
      background={noAuthPages.background}
      overflow="hidden"
      height="100vh"
      direction="column"
      style={{ minHeight: '100vh', maxHeight: '100vh' }}
      {...rest}
    >
      <Box
        flex
        fill
        justify={justifyContent}
        data-bg={noAuthPages.bgImage}
        background={noAuthPages.bgImage && { image: small ? `url(${noAuthPages.bgImage}&w=1024)` : `url(${noAuthPages.bgImage})` }}
        margin={{ bottom: hideFooter ? '0' : '-5px' }}
      >
        <Seo {...seoConfig} />
        <Container
          align="center"
          justify={justifyContent}
          pad={{ horizontal: small ? 'var(--vw5p725)' : 'var(--vw7p725)', bottom: bottomPad }}
          margin={{ top: contentTopMargin || '0' }}
        >
          <StyledForm
            pad="1.5rem"
            align="center"
            justify="start"
            gap={customGap || '1.25rem'}
            customWidth={customWidth}
          >
            {renderHeader()}
            <Box flex justify="end" width="100%" pad={{ bottom: removeLowerPad ? '0' : '1rem' }}>
              {children}
            </Box>
          </StyledForm>
        </Container>
      </Box>
      {!hideFooter && (
        <Box style={{ flexShrink: 0 }}>
          <Footer small={small} darkModeOverride={noAuthPages.darkBg} />
        </Box>
      )}
    </Box>
  );
};

NoAuthLayout.propTypes = {
  seoConfig: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
  }),
  small: PropTypes.bool.isRequired,
  withBackButton: PropTypes.bool,
  backButtonLabel: PropTypes.string,
  backButtonPath: PropTypes.string,
  backButtonClick: PropTypes.func,
  bottomPad: PropTypes.string,
  title: PropTypes.string.isRequired,
  subTitle: PropTypes.string,
  children: PropTypes.node.isRequired,
  customWidth: PropTypes.string,
  customTitle: PropTypes.bool,
  customGap: PropTypes.string,
  justifyContent: PropTypes.string,
  contentTopMargin: PropTypes.string,
  removeLowerPad: PropTypes.bool,
  altHeaderStyle: PropTypes.bool,
  hideFooter: PropTypes.bool,
};

export default NoAuthLayout;
